// Aggregations for the multi-year "big picture" training view. Pure functions
// over the slimmed /history payload (activities + daily wellness).

export interface HistActivity {
  id: string;
  type: string;
  start_date_local: string;
  distance: number | null;
  moving_time: number;
  total_elevation_gain: number | null;
  average_heartrate: number | null;
  icu_training_load: number | null;
  icu_lthr: number | null;
  icu_hr_zone_times: number[] | null;
}

export interface HistWellness {
  id: string;
  ctl: number | null;
  atl: number | null;
  hrv: number | null;
  restingHR: number | null;
  weight: number | null;
  vo2max: number | null;
  sleepSecs: number | null;
}

export type SportGroup = "run" | "bike" | "ski" | "swim" | "strength" | "other";

export const SPORT_GROUPS: { key: SportGroup; label: string; color: string }[] = [
  { key: "run", label: "Löpning", color: "#e2b31a" },
  { key: "bike", label: "Cykel", color: "#4aa3df" },
  { key: "ski", label: "Skidor", color: "#7fd39b" },
  { key: "swim", label: "Simning", color: "#8a7bff" },
  { key: "strength", label: "Styrka", color: "#e0503f" },
  { key: "other", label: "Övrigt", color: "#7a7a7a" },
];

export function sportGroup(type: string): SportGroup {
  const t = (type || "").toLowerCase();
  if (t.includes("alpine") || t.includes("snowboard")) return "other";
  if (t.includes("ski")) return "ski";
  if (t.includes("swim")) return "swim";
  if (t.includes("run")) return "run";
  if (t.includes("ride") || t.includes("bike")) return "bike";
  if (/weight|workout|crossfit|strength|yoga|pilates/.test(t)) return "strength";
  return "other";
}

const DAY = 86400000;
const toMs = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};
const fmtMs = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function weekStartOf(s: string): string {
  const ms = toMs(s);
  const dow = (new Date(ms).getUTCDay() + 6) % 7; // Mon = 0
  return fmtMs(ms - dow * DAY);
}

export function shiftYears(dateStr: string, n: number): string {
  const d = new Date(toMs(dateStr));
  d.setUTCFullYear(d.getUTCFullYear() + n);
  return d.toISOString().slice(0, 10);
}

// [low (Z1-2), mid (Z3), high (Z4+)] seconds. Uses HR-zone times when present,
// otherwise falls back to the session's average HR vs LTHR.
function intensity(a: HistActivity): [number, number, number] {
  const dur = a.moving_time || 0;
  const z = a.icu_hr_zone_times;
  if (Array.isArray(z) && z.length >= 4) {
    let low = 0, mid = 0, high = 0;
    z.forEach((v, i) => {
      const s = v || 0;
      if (i < 2) low += s; else if (i === 2) mid += s; else high += s;
    });
    if (low + mid + high > 0) return [low, mid, high];
  }
  if (a.average_heartrate && dur > 0) {
    const p = a.average_heartrate / (a.icu_lthr ?? 177);
    return p < 0.85 ? [dur, 0, 0] : p < 0.93 ? [0, dur, 0] : [0, 0, dur];
  }
  return [0, 0, 0];
}

export interface Bucket {
  secs: number;
  km: number;
  elev: number;
  tss: number;
  sessions: number;
  low: number;
  mid: number;
  high: number;
  bySport: number[]; // seconds, aligned with SPORT_GROUPS
}

function emptyBucket(): Bucket {
  return { secs: 0, km: 0, elev: 0, tss: 0, sessions: 0, low: 0, mid: 0, high: 0, bySport: SPORT_GROUPS.map(() => 0) };
}

function addTo(b: Bucket, a: HistActivity) {
  const secs = a.moving_time || 0;
  b.secs += secs;
  b.km += (a.distance || 0) / 1000;
  b.elev += a.total_elevation_gain || 0;
  b.tss += a.icu_training_load || 0;
  b.sessions += 1;
  const [lo, mi, hi] = intensity(a);
  b.low += lo; b.mid += mi; b.high += hi;
  const gi = SPORT_GROUPS.findIndex((g) => g.key === sportGroup(a.type));
  b.bySport[gi] += secs;
}

export const hours = (secs: number) => secs / 3600;

export interface WeekBucket extends Bucket { start: string }

export function buildWeeks(acts: HistActivity[], fromStr: string, toStr: string): WeekBucket[] {
  const map = new Map<string, WeekBucket>();
  for (let t = toMs(weekStartOf(fromStr)); t <= toMs(weekStartOf(toStr)); t += 7 * DAY) {
    const start = fmtMs(t);
    map.set(start, { start, ...emptyBucket() });
  }
  for (const a of acts) {
    const w = map.get(weekStartOf(a.start_date_local));
    if (w) addTo(w, a);
  }
  return Array.from(map.values());
}

export interface MonthBucket extends Bucket { key: string; year: number; month: number } // month 0-11

export function buildMonths(acts: HistActivity[], fromStr: string, toStr: string): MonthBucket[] {
  const out: MonthBucket[] = [];
  const map = new Map<string, MonthBucket>();
  let y = Number(fromStr.slice(0, 4));
  let m = Number(fromStr.slice(5, 7)) - 1;
  const endKey = toStr.slice(0, 7);
  for (;;) {
    const key = `${y}-${String(m + 1).padStart(2, "0")}`;
    if (key > endKey) break;
    const b: MonthBucket = { key, year: y, month: m, ...emptyBucket() };
    out.push(b);
    map.set(key, b);
    if (++m > 11) { m = 0; y++; }
  }
  for (const a of acts) {
    const b = map.get(a.start_date_local.slice(0, 7));
    if (b) addTo(b, a);
  }
  return out;
}

export interface PeriodSummary extends Bucket {
  label: string;
  range: string;
  weeks: number;
}

const MONTH_SHORT = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
export const monthShort = (m: number) => MONTH_SHORT[m];

function rangeLabel(fromExcl: string, to: string): string {
  const from = fmtMs(toMs(fromExcl) + DAY);
  const f = (s: string) => `${MONTH_SHORT[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}`;
  return `${f(from)} – ${f(to)}`;
}

// Rolling 12-month windows ending today: k = 0 is the latest 12 months.
export function buildPeriods(acts: HistActivity[], today: string, count = 3): PeriodSummary[] {
  const labels = ["Senaste 12 mån", "12–24 mån sedan", "24–36 mån sedan", "36–48 mån sedan", "48–60 mån sedan"];
  const out: PeriodSummary[] = [];
  for (let k = 0; k < count; k++) {
    const to = shiftYears(today, -k);
    const fromExcl = shiftYears(today, -(k + 1));
    const b = emptyBucket();
    for (const a of acts) {
      const d = a.start_date_local.slice(0, 10);
      if (d > fromExcl && d <= to) addTo(b, a);
    }
    out.push({ ...b, label: labels[k] ?? `${k * 12}–${(k + 1) * 12} mån sedan`, range: rangeLabel(fromExcl, to), weeks: 52 });
  }
  return out;
}

// Calendar-year overlay: monthly hours per year, null where the month lies
// outside the data window (before the first full month, or after today).
export function yearOverlay(months: MonthBucket[], fromStr: string, toStr: string) {
  const startKey = fromStr.slice(0, 7);
  const endKey = toStr.slice(0, 7);
  const startPartial = fromStr.slice(8, 10) !== "01";
  const years = Array.from(new Set(months.map((m) => m.year))).sort();
  return years.map((year) => ({
    year,
    values: Array.from({ length: 12 }, (_, mi) => {
      const b = months.find((x) => x.year === year && x.month === mi);
      if (!b) return null;
      if (b.key < startKey || b.key > endKey) return null;
      if (b.key === startKey && startPartial) return null;
      return hours(b.secs);
    }),
  }));
}

export interface WellnessMonthly {
  ctl: (number | null)[];      // end-of-month fitness
  restingHR: (number | null)[]; // monthly mean
  hrv: (number | null)[];
  weight: (number | null)[];
}

export function monthlyWellness(w: HistWellness[], months: MonthBucket[]): WellnessMonthly {
  interface Acc { rhr: number[]; hrv: number[]; weight: number[]; ctl: number | null }
  const sums = new Map<string, Acc>();
  const sorted = [...w].sort((a, b) => (a.id < b.id ? -1 : 1));
  for (const d of sorted) {
    const key = d.id.slice(0, 7);
    let s = sums.get(key);
    if (!s) { s = { rhr: [], hrv: [], weight: [], ctl: null }; sums.set(key, s); }
    if (d.restingHR) s.rhr.push(d.restingHR);
    if (d.hrv) s.hrv.push(d.hrv);
    if (d.weight) s.weight.push(d.weight);
    if (d.ctl != null) s.ctl = d.ctl;
  }
  const mean = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
  const pick = (f: (s: Acc) => number | null) =>
    months.map((m) => { const s = sums.get(m.key); return s ? f(s) : null; });
  return {
    ctl: pick((s) => s.ctl),
    restingHR: pick((s) => mean(s.rhr)),
    hrv: pick((s) => mean(s.hrv)),
    weight: pick((s) => mean(s.weight)),
  };
}

export interface Highlights {
  peakCtl: { v: number; date: string } | null;
  bigWeek: { hours: number; start: string } | null;
  bigMonth: { hours: number; key: string } | null;
  longest: { hours: number; date: string; type: string } | null;
  weeksTrainedPct: number;
  longestBreakDays: number;
}

export function buildHighlights(
  acts: HistActivity[], wellness: HistWellness[], weeks: WeekBucket[], months: MonthBucket[], today: string,
): Highlights {
  let peakCtl: Highlights["peakCtl"] = null;
  for (const d of wellness) {
    if (d.ctl != null && (!peakCtl || d.ctl > peakCtl.v)) peakCtl = { v: d.ctl, date: d.id };
  }
  let bigWeek: Highlights["bigWeek"] = null;
  for (const w of weeks) if (w.secs > 0 && (!bigWeek || hours(w.secs) > bigWeek.hours)) bigWeek = { hours: hours(w.secs), start: w.start };
  let bigMonth: Highlights["bigMonth"] = null;
  for (const m of months) if (m.secs > 0 && (!bigMonth || hours(m.secs) > bigMonth.hours)) bigMonth = { hours: hours(m.secs), key: m.key };
  let longest: Highlights["longest"] = null;
  for (const a of acts) {
    const h = hours(a.moving_time || 0);
    if (!longest || h > longest.hours) longest = { hours: h, date: a.start_date_local.slice(0, 10), type: a.type };
  }
  const trained = weeks.filter((w) => w.sessions > 0).length;
  const days = Array.from(new Set(acts.map((a) => a.start_date_local.slice(0, 10)))).sort();
  let longestBreakDays = 0;
  for (let i = 1; i < days.length; i++) {
    longestBreakDays = Math.max(longestBreakDays, Math.round((toMs(days[i]) - toMs(days[i - 1])) / DAY) - 1);
  }
  if (days.length) longestBreakDays = Math.max(longestBreakDays, Math.round((toMs(today) - toMs(days[days.length - 1])) / DAY));
  return {
    peakCtl, bigWeek, bigMonth, longest,
    weeksTrainedPct: weeks.length ? Math.round((trained / weeks.length) * 100) : 0,
    longestBreakDays,
  };
}

// Trailing mean over `n` points (for the smoothed weekly-volume line).
export function rollingMean(values: number[], n: number): number[] {
  return values.map((_, i) => {
    const from = Math.max(0, i - n + 1);
    const slice = values.slice(from, i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}
